import { useState, useEffect } from 'react'
import {
  Context,
  enableDebugConsoleLogDrain,
  enableContextDevTools,
  CookieTransitionDataStore
} from '@uniformdev/context'
import { fetchUniformManifest } from '../../helpers/fetchUniformManifest'
import { updateEnrichment, updateQuirks } from '../../helpers/updateEnrichment'
import { hasConsentForCategory } from '../../helpers/checkCookieConsent'
import { CONSENT_CATEGORIES } from '../../config/cookie-consent'

const isArgos = process.env.BRAND === 'ARGOS'

const setUniformContext = manifest => {
  if (!manifest) return null

  const plugins = [enableContextDevTools()]
  if (process.env.NODE_ENV !== 'production') {
    plugins.push(enableDebugConsoleLogDrain('debug'))
  }

  return new Context({
    defaultConsent: false,
    transitionStore: new CookieTransitionDataStore({}),
    manifest,
    plugins
  })
}

export const viewedProductsQuirk = (context, productId) => {
  const viewedProducts = context?.quirks?.viewedProducts
  const viewedProductsArray = viewedProducts?.length > 0 ? viewedProducts?.split(', ') : []

  const maxLength = 50
  const productIdIndex = viewedProductsArray.indexOf(productId)

  if (productIdIndex !== -1) {
    viewedProductsArray.splice(productIdIndex, 1)
  }

  viewedProductsArray.unshift(productId)

  if (viewedProductsArray.length > maxLength) {
    viewedProductsArray.length = maxLength
  }

  return {
    viewedProducts: viewedProductsArray.join(', ')
  }
}

export const UniformTracking = ({ brand, catId, l2CatId, l3CatId, productId, trolleyItemsAdded }) => {
  const [context, setContext] = useState(null)

  const sanitisedKey = str => {
    return str.replace(' ', '-').toLowerCase()
  }

  const enrichments = [
    {
      str: 15,
      cat: 'pdp-category',
      key: catId
    },
    {
      str: 15,
      cat: 'plp-l2',
      key: l2CatId
    },
    {
      str: 15,
      cat: 'plp-l3',
      key: l3CatId
    },
    {
      str: 15,
      cat: 'pdp-v',
      key: productId
    },
    {
      str: 10,
      cat: 'brands',
      key: sanitisedKey(brand)
    }
  ].filter(enrichment => enrichment.key)

  const updateUniformTracking = () => {
    updateEnrichment(enrichments, context)
    updateQuirks(viewedProductsQuirk(context, productId), context)
  }

  useEffect(() => {
    async function getUniformManifest() {
      if (isArgos) {
        const manifest = await fetchUniformManifest(window?.UNIFORM_MANIFEST_CDN_URL)
        setContext(setUniformContext(manifest))
      }
    }
    getUniformManifest()
  }, [])

  useEffect(() => {
    // Use platform brand (ARGOS, HABITAT, TU) not product brand
    const platformBrand = process.env.BRAND
    const hasConsent = hasConsentForCategory(platformBrand, CONSENT_CATEGORIES.MARKETING)
    const updateConsent = async () => {
      await context?.storage.updateData([
        {
          type: 'consent',
          data: !!hasConsent // Convert null to false for uniform context
        }
      ])
    }

    updateConsent()
  }, [context])

  useEffect(() => {
    if (context) {
      updateUniformTracking()
    }
  }, [context, productId])

  useEffect(() => {
    if (context && trolleyItemsAdded?.length > 0) {
      const attEnrichments = trolleyItemsAdded
        .filter((item, index) => trolleyItemsAdded.indexOf(item) === index)
        .map(item => {
          return {
            str: 100,
            cat: 'p-att',
            key: item
          }
        })

      updateEnrichment(attEnrichments, context)
    }
  }, [trolleyItemsAdded, context])

  return null
}
